import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CtaBand } from "../components/CtaBand";
import { WoodSwatch } from "../components/WoodSwatch";
import { PRODUCT_FEATURES, API_URL } from "../config";

const BoardPhoto = ({ colours }) => {
  const c = colours.find((x) => x.name === "Natural Oak" && x.style !== "herringbone") || colours.find((x) => x.image_url);
  if (!c?.image_url) return <WoodSwatch light="#C89B62" dark="#7C4F24" seed={11} className="w-full aspect-[4/3]" />;
  return (
    <div className="relative w-full aspect-[4/3] overflow-hidden group" data-testid="board-photo">
      <motion.img
        src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`}
        alt={`${c.name} 7.5 mm hybrid flooring board close-up`}
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ scale: 1.15 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      />
      <div className="absolute bottom-4 left-4 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-xs font-semibold text-[#4A3018]">
        Shown: {c.name}
      </div>
    </div>
  );
};

const SPECS = [
  { label: "Flooring type", value: "Hybrid flooring" },
  { label: "Total thickness", value: "7.5 mm" },
  { label: "Core", value: "Rigid SPC core" },
  { label: "Underlay", value: "Pre-attached underlay" },
  { label: "Installation method", value: "Click-lock floating floor" },
  { label: "Finish", value: "Timber-look, deep embossed textured surface" },
  { label: "Suitable for", value: "Living rooms, bedrooms, hallways, kitchens and other suitable internal areas" },
  { label: "Colours", value: "Available in a selection of modern timber colours" },
];

export default function HybridFlooring() {
  const [colours, setColours] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/colours`).then((r) => r.json()).then(setColours).catch(() => {});
  }, []);
  return (
    <div data-testid="hybrid-flooring-page">
      <PageHeader
        label="Hybrid Flooring Melbourne"
        title="7.5 mm hybrid flooring, made for everyday homes"
        description="Our main product is a 7.5 mm hybrid flooring board that gives you the appearance of natural timber with the strength and practicality needed for everyday homes."
        testId="hybrid-flooring-header"
      />

      <section className="py-20 md:py-24" data-testid="product-overview">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold">What is hybrid flooring?</h2>
            <p className="mt-5 text-base text-[#525252] leading-relaxed">
              Hybrid flooring combines a rigid SPC (stone plastic composite) core with a timber-look surface,
              giving you the warmth of timber with a board that is stable and straightforward to live with.
              The underlay comes pre-attached, and the click-lock system means the floor floats over a suitable,
              prepared subfloor — no glue or nails needed.
            </p>
            <p className="mt-4 text-base text-[#525252] leading-relaxed">
              It is a practical, affordable hybrid flooring option for residential flooring in Melbourne homes,
              from living rooms and bedrooms to hallways and kitchens.
            </p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3" data-testid="product-features-list">
              {PRODUCT_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm font-medium text-[#1C1C1C]">
                  <Check className="w-4 h-4 text-[#C87A38] shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/colours"
                className="inline-flex items-center gap-2 rounded-full bg-[#C87A38] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A6632B] focus:outline-none focus:ring-2 focus:ring-[#C87A38] focus:ring-offset-2"
                data-testid="hybrid-view-colours-button"
              >
                Browse flooring colours <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/accessories"
                className="inline-flex items-center gap-2 rounded-full border border-[#4A3018]/20 px-6 py-3.5 text-sm font-semibold text-[#4A3018] transition-colors hover:bg-[#4A3018] hover:text-white"
                data-testid="hybrid-view-accessories-button"
              >
                Scotia &amp; trims
              </Link>
            </div>
          </div>
          <div className="border border-[#E5E5E5] rounded-2xl overflow-hidden bg-white" data-testid="spec-table-card">
            <div className="bg-[#4A3018] px-7 py-5">
              <h3 className="text-white font-semibold font-heading text-lg">Product information</h3>
            </div>
            <dl className="divide-y divide-[#E5E5E5]">
              {SPECS.map((s) => (
                <div key={s.label} className="px-7 py-4 grid grid-cols-3 gap-4" data-testid={`spec-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <dt className="text-sm font-semibold text-[#4A3018]">{s.label}</dt>
                  <dd className="text-sm text-[#525252] col-span-2">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAFA] py-20 md:py-24" data-testid="board-layers-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E5]">
            <BoardPhoto colours={colours} />
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">Inside the board</p>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">A closer look at 7.5 mm SPC hybrid flooring</h2>
            <ul className="mt-8 space-y-6">
              {[
                { title: "Deep embossed timber-look surface", text: "A realistic timber-look finish with deep embossing texture, in a range of modern colours." },
                { title: "Rigid SPC core", text: "A dense, rigid core that keeps boards stable under everyday use." },
                { title: "Pre-attached underlay", text: "Built-in underlay helps with underfoot comfort and makes installation simpler." },
                { title: "Click-lock edges", text: "Boards lock together and float over a prepared subfloor for a clean install." },
              ].map((layer, i) => (
                <li key={layer.title} className="flex gap-5 fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`layer-${i}`}>
                  <span className="text-2xl font-bold font-heading text-[#C87A38]/30 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-lg font-semibold font-heading">{layer.title}</h3>
                    <p className="mt-1 text-sm text-[#525252] leading-relaxed">{layer.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CtaBand title="Not sure if hybrid suits your home?" text="Send us a few photos and your rough measurements — we will give you straight advice and a clear flooring installation quote." />
    </div>
  );
}
